import axios from 'axios';
import BaseApi from '../BaseApi';
import {
	ParticipantType,
	EditTaskType,
	EditTaskDataModel,
	TaskTemplateType,
	TransitionCompleteResponse,
} from 'src/Types/Task';
import SendSoapAPI from './SendSoap';
import SimpleTaskAPI from './SimpleTask';
import ResultNoteTaskAPI from './ResultNoteTask';
import UploadDocsFormAPI from './UploadDocsForm';
import ZipAttachsByMR_API from './ZipAttachsByMR';
import { ITaskLeftSidePave } from 'src/Types/File';
import { SendingFormType } from 'Src/Types/Task/SendMailTaskType';
import RdcSoapApi from './RdcSoap';
import GetSmevRequestApi from './GetSmevRequest';
import RosreestrZipAPI from './RosreestrZip';

/**
 * API для работы с Задачами
 */
export default class APITask extends BaseApi {
	static SendSoap = SendSoapAPI;
	static SimpleTask = SimpleTaskAPI;
	static ResultNoteTask = ResultNoteTaskAPI;
	static UploadDocsForm = UploadDocsFormAPI;
	static ZipAttachsByMR = ZipAttachsByMR_API;
	static RdcSoap = RdcSoapApi;
	static GetSmevRequest = GetSmevRequestApi;
	static RosreestrZip = RosreestrZipAPI;

	static BusinessMail = {
		startTask: async (
			fileId: string,
			componentId: string,
		): Promise<TransitionCompleteResponse> => {
			const url = window.host + '/BusinessMail/StartTask';
			const data = { fileId, componentId };
			try {
				const request = await axios.post(url, data, {
					withCredentials: true,
				});
				if (this.checkUnauthorized(request)) {
					throw new Error('Ошибка при старте задачи');
				} else {
					return request.data;
				}
			} catch (error: any) {
				throw new Error('Ошибка при старте задачи');
			}
		},
	};

	static SendMail = {
		send: async (
			data: SendingFormType,
		): Promise<TransitionCompleteResponse> => {
			const url = window.host + '/SendEmail/Send';
			try {
				const request = await axios.post(url, data, {
					withCredentials: true,
				});
				if (this.checkUnauthorized(request)) {
					throw new Error('Ошибка при отправки эл. почты');
				} else {
					return request.data;
				}
			} catch (error: any) {
				console.warn(error);
				throw new Error('Ошибка при отправки эл. почты');
			}
		},
		cancel: async (
			fileId: string,
			taskId: string,
			componentId: string,
		): Promise<TransitionCompleteResponse> => {
			const url = window.host + '/BusinessMail/StartTask';
			const data = { fileId, taskId, componentId };
			try {
				const request = await axios.post(url, data, {
					withCredentials: true,
				});
				if (this.checkUnauthorized(request)) {
					throw new Error('Ошибка при отмене задачи');
				} else {
					return request.data;
				}
			} catch (error: any) {
				throw new Error('Ошибка при отмене задачи');
			}
		},
	};

	static Velocity = {
		getVariables: async (
			fileId: string,
			componentId: string,
		): Promise<any> => {
			const url = window.host + '/VelocityTest/Variables';
			try {
				const request = await axios.post(
					url,
					{ fileId, componentId },
					{ withCredentials: true },
				);
				return request.data;
			} catch (error: any) {
				console.warn(error);
				throw new Error(
					'VelocityTest. Ошибка запроса на получении списка переменных контекста',
				);
			}
		},
		check: async (
			fileId: string,
			componentId: string,
			velocityTemplate: string,
		): Promise<any> => {
			const url = window.host + '/VelocityTest/Check';
			try {
				const request = await axios.post(
					url,
					{ fileId, componentId, velocityTemplate },
					{ withCredentials: true },
				);
				return request.data;
			} catch (error: any) {
				console.warn(error);
				throw new Error(
					'VelocityTest. Ошибка при отправки шаблона на проверку',
				);
			}
		},
	};

	static getParticipants = async (
		search: string,
	): Promise<Array<ParticipantType>> => {
		const url = `${window.host}/WebInterfaceModule/Search/GetParticipants?term=${search}&takeCount=3&skipPageCount=0`;
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении списка пользователей.');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении списка пользователей');
		}
	};

	/** Запрос за данными для редактирования основных данных задачи  */
	static getEditTaskData = async (
		fileId: string,
		componentId: string,
	): Promise<EditTaskType> => {
		const url = `${window.host}/WebInterfaceModule/File/EditTaskData?fileId=${fileId}&componentId=${componentId}`;
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении данных задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении данных задачи');
		}
	};

	/** Редактирование исполнителя и срока выполнения задачи */
	static saveTaskDataExecute = async (
		data: EditTaskDataModel,
	): Promise<any> => {
		const url =
			window.host + '/WebInterfaceModule/File/EditTaskDataExecute';
		try {
			const request = await axios.post(url, data, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка сохранения данных задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка сохранения данных задачи');
		}
	};

	/** Запрос за данными о задаче */
	static getData = async (
		fileId: string,
		pave: ITaskLeftSidePave,
	): Promise<TaskTemplateType> => {
		const url = `${window.host}${pave.path}?fileId=${fileId}&componentId=${pave.componentId}${pave.formPath ? '&form=' + pave.formPath : ''}`;
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении данных задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении данных задачи');
		}
	};

	/**  */
	static someAction = async (
		urlPart: string,
		fileId: string,
		componentId: string,
		taskId?: string,
		editionData?: any,
	): Promise<TransitionCompleteResponse> => {
		const url = window.host + urlPart;
		const data = { fileId, componentId, taskId, ...editionData };
		try {
			const request = await axios.post(url, data, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error(
					'Ошибка при кастомном запросе. Запрос - ' + urlPart,
				);
			} else {
				return request.data;
			}
		} catch (error: any) {
			console.warn(error);
			throw new Error(
				'Ошибка при кастомном запросе. Запрос - ' + urlPart,
			);
		}
	};
}
